import { useEffect, useState } from 'react'

import { useQuery } from '@apollo/client'
import { CommonClose2Icon } from '@web/../assets/icons'

import FlickingArea from '@web/components/FlickingArea/FlickingArea'
import { gql } from '@web/gql'
import { ReactionModal_ReactionStatFragment, ReviewGroupReactionSort } from '@web/gql/graphql'
import useInfiniteScroll from '@web/hooks/useInfiniteScroll'
import { extractByTypename } from '@web/libs/apollo/typeGuard'
import { formatCountByThousandUnit } from '@web/libs/formatter'
import { reactionTypeMap } from '@web/types/reaction-type'

import Clickable from '../../Clickable'
import Loading from '../../Loading'
import ModalContainer from '../../Modal/ModalContainer'
import { ModalComponent } from '../../Modal/types'
import { openToast } from '../../Toast/GlobalToastPortal'
import { ReactionNClickArea } from '../reaction.constant'
import styles from './ReactionModal.module.scss'
import ReactionUser from './ReactionUser/ReactioinUser'

const ReviewGroupReactionsQuery = gql(`
  query ReviewGroupReactions($input: ReviewGroupReactionsConnectionInput!) {
    reviewGroupReactions(input: $input) {
      ... on ReviewGroupReactionsConnection {
        edges {
          reaction {
            id
            user {
              id
            }
            ...ReactionUser_Reaction
          }
        }
        pageInfo {
          hasNextPage
          endCursor
        }
      }
      ... on BaseError {
        message
      }
    }
  }
`)

export interface ReactionModalProps {
  reviewGroupId: string
  reactionStat?: ReactionModal_ReactionStatFragment | null
  nClickArea: ReactionNClickArea
}

const ReactionModal: ModalComponent<ReactionModalProps> = ({
  showModal,
  cancelModal,
  reviewGroupId,
  reactionStat,
  nClickArea,
}) => {
  const [selectedReactionType, setSelectedReactionType] = useState<string>()

  const queryInput = {
    reviewGroupId,
    sort: ReviewGroupReactionSort.CreatedDateTimeDesc,
    reactionType: selectedReactionType as string,
    first: 10,
  }

  const { data, loading, fetchMore } = useQuery(ReviewGroupReactionsQuery, {
    variables: {
      input: queryInput,
    },
    skip: !reviewGroupId || !selectedReactionType,
  })

  useEffect(() => {
    const topReaction = reactionStat?.reactionCounts[0]
    if (topReaction?.reactionType && topReaction.count !== 0) {
      setSelectedReactionType(topReaction.reactionType)
    }
  }, [reactionStat])

  const onClickReactionType = (reactionType: string, reactionCount: number) => {
    if (reactionCount) {
      return setSelectedReactionType(reactionType)
    }
    return openToast({
      title: (
        <>
          <em>{reactionTypeMap.get(reactionType)?.label.ko}</em> 반응을 남긴 분이 없어요.
        </>
      ),
      iconType: 'error',
    })
  }

  const { ReviewGroupReactionsConnection } = extractByTypename(data?.reviewGroupReactions)
  const { edges, pageInfo } = ReviewGroupReactionsConnection || {}

  const FetchMoreTrigger = useInfiniteScroll({
    checkLastElement: () => !ReviewGroupReactionsConnection?.pageInfo.hasNextPage,
    fetchMore: () =>
      fetchMore({
        variables: {
          input: {
            ...queryInput,
            after: ReviewGroupReactionsConnection?.pageInfo.endCursor,
          },
        },
      }),
  })

  return (
    <ModalContainer showModal={showModal} position="bottom" className={styles.modal_reaction_list}>
      <>
        <div className={styles.header_area}>
          <div className={styles.modal_tit_area}>
            {formatCountByThousandUnit(reactionStat?.totalCount ?? 0)}명이 반응을 남겼어요
          </div>
          {/* [D] packages/web/pages/reactions/ReactionTab.tsx 적용가능하다면 아래 filter_area 대신 ReactionTab으로 적용해주세요. */}
          {/* [D] ReactionTab 적용시, 모달에선 '전체'필터 제외 */}
          <div className={styles.filter_area_wrapper}>
            <div className={styles.filter_area}>
              <FlickingArea
                options={{
                  align: 'prev',
                }}
              >
                {reactionStat?.reactionCounts.map(reactionCount => {
                  const { count, reactionType } = reactionCount
                  const reactionDisplayInfo = reactionTypeMap.get(reactionType)

                  if (!reactionDisplayInfo) return null

                  const { label, name, emojiUrl } = reactionDisplayInfo

                  const isActive = name === selectedReactionType

                  if (count === 0) return null

                  return (
                    <span className={styles.btn_wrapper} key={name}>
                      <Clickable
                        className={styles.btn}
                        aria-pressed={isActive}
                        onClick={() => onClickReactionType(name, count)}
                        nClickCode={nClickArea?.EMOJITAB}
                      >
                        <div className={styles.thumb_area}>
                          <div className="place_thumb">
                            <img className={styles.img} src={emojiUrl} width="18" height="18" alt="" />
                          </div>
                        </div>
                        <span className={styles.name}>{label.ko}</span>
                        {count && <span className={styles.count}>{formatCountByThousandUnit(count)}</span>}
                      </Clickable>
                    </span>
                  )
                })}
              </FlickingArea>
            </div>
          </div>
        </div>

        <ul className={styles.reactor_list}>
          {loading ? (
            <Loading height={258} />
          ) : (
            <>
              {edges?.map(({ reaction }) => (
                <ReactionUser key={reaction.id} nClickArea={nClickArea} reaction={reaction} />
              ))}
              <FetchMoreTrigger />
              {pageInfo?.hasNextPage && <Loading height={80} />}
            </>
          )}
        </ul>
        <Clickable className={styles.btn_close} onClick={cancelModal}>
          <span className="place_blind">모달 닫기</span>
          <CommonClose2Icon className={styles.icon} aria-hidden="true" />
        </Clickable>
      </>
    </ModalContainer>
  )
}

ReactionModal.fragments = {
  reactionStat: gql(`
    fragment reactionModal_reactionStat on ReactionStat {
      id
      totalCount
      reactionCounts {
        count
        reactionType
      }
    }
  `),
}

export default ReactionModal
